import { useState, useEffect, useCallback } from "react";
import "./trainers.css";
import Table from "../Table/Table";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Bounce, toast } from "react-toastify";
import { buildFilterQueryParams } from "../utils/queryHelpers";
import useDebouncedValue from "../hooks/useDebouncedValue";

const formatTrainer = (trainer) => {
  const { anagrafica } = trainer;
  const date = new Date(anagrafica.dataDiNascita);
  const formattedDate = `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${date.getFullYear()}`;
  return {
    id: anagrafica.id,
    nome: anagrafica.nome,
    cognome: anagrafica.cognome,
    email: anagrafica.email,
    codiceFiscale: anagrafica.codiceFiscale,
    cellulare: anagrafica.cellulare,
    cittaDiResidenza: anagrafica.cittàDiResidenza,
    dataDiNascita: formattedDate,
    _raw: trainer,
  };
};

const OperatoreTrainers = () => {
  const [loading, setLoading] = useState(false);
  const [trainers, setTrainers] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({});
  const debouncedSorting = useDebouncedValue(sorting, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);
  const navigate = useNavigate();

  const fetchTrainers = useCallback(
    async ({
      pageParam = page,
      sizeParam = pageSize,
      sortingParam = debouncedSorting,
      filtersParam = debouncedColumnFilters,
      filterFnsParam = columnFilterFns,
    } = {}) => {
      setLoading(true);
      try {
        const sortStrings = (sortingParam ?? []).map(
          ({ id, desc }) => `${id},${desc ? "desc" : "asc"}`
        );
        const filterParams = buildFilterQueryParams(filtersParam, filterFnsParam);
        const params = {
          page: pageParam,
          size: sizeParam,
          ...filterParams,
          ...(sortStrings.length > 0 && { sort: sortStrings.join(";") }),
        };
        const response = await api.get("/associazioni/trainers/available", {
          params,
        });
        const rawData = Array.isArray(response.data?.content)
          ? response.data.content
          : Array.isArray(response.data)
          ? response.data
          : [];
        const pageInfo = response.data?.page;
        setTrainers(rawData.map(formatTrainer));
        setTotalRows(pageInfo?.totalElements ?? rawData.length);
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns]
  );

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "OPERATOR") {
      alert("Accedi come operatore per visualizzare i trainers!");
      navigate("/login");
      localStorage.clear();
    }
  }, [navigate]);

  useEffect(() => {
    if (localStorage.getItem("userRole") !== "OPERATOR") return;
    fetchTrainers({ pageParam: page, sizeParam: pageSize });
  }, [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns, fetchTrainers]);

  const columns = [
    { field: "codiceFiscale", header: "Codice Fiscale" },
    { field: "cognome", header: "Cognome" },
    { field: "nome", header: "Nome" },
    { field: "cellulare", header: "Cell" },
    { field: "email", header: "Mail" },
    { field: "cittaDiResidenza", header: "Città di Residenza" },
  ];

  const handlePageChange = (pageNumber) => {
    setPage(Math.max(pageNumber - 1, 0));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setPage(0);
  };

  const handleColumnFiltersChange = (newFilters) => {
    setColumnFilters(newFilters);
    setPage(0);
  };

  const handleColumnFilterFnsChange = (newFilterFns) => {
    setColumnFilterFns(newFilterFns);
    setPage(0);
  };

  const handleRowClick = (trainer) => {
    navigate(`/docente/disponibile/${trainer.id}`, {
      state: { trainer: trainer._raw },
    });
  };

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">Docenti</div>
      </div>

      <div className="page-content trainers">
        <Table
          columns={columns}
          data={trainers}
          onRowClick={handleRowClick}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRows={totalRows}
          perPage={pageSize}
          currentPage={page + 1}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          columnFilterFns={columnFilterFns}
          onColumnFilterFnsChange={handleColumnFilterFnsChange}
        />
      </div>
    </div>
  );
};

export default OperatoreTrainers;
